import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Badge, Button, Drawer, Popconfirm, Segmented, Select, Space, Spin, Table, Tag, Timeline, Tooltip, Typography, message } from "antd";
import {
    REVIEW_ACTIONS,
    REVIEW_STATUSES,
    REVIEW_TYPES,
    canTakeAction,
    lockJobPhrase,
    reviewReasonCodeLabel,
    reviewReasonLabel,
    reviewStatusColor,
    reviewTypeHelp,
    reviewTypeLabel,
} from "common/reviewQueue";
import ReviewItemActionModal from "components/molecules/ReviewItemActionModal";
import ReviewItemComparison from "components/molecules/ReviewItemComparison";
import SubsumedRecords from "components/organisms/SubsumedRecords";
import AppPaths from "constants/appPaths";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { getGoldenStewardshipPermissionRequest } from "store/goldenNominal/actions";
import {
    breakSchemaJobLockRequest,
    clearReviewItemActionResult,
    clearReviewQueueItem,
    clearSchemaJobLockBreakResult,
    resetReviewQueue,
    getReviewQueueCountsRequest,
    getReviewQueueItemRequest,
    getReviewQueueItemsRequest,
    getSchemaJobLockRequest,
    reviewItemActionRequest,
} from "store/reviewQueue/actions";

interface IReviewQueueProps {
    tenant: string;
    // Business area and schema names, as in the URL.
    businessArea: string;
    schema: string;
    userBusinessArea?: { businessAreaId: string };
    setBreadcrumbNavigationItems?: (items: { route: string; text: string }[]) => void;
}

const formatDate = (value?: string | null) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "");

// Wide enough for several records side by side, never wider than the window.
const drawerWidth = () => Math.min(1400, Math.round((typeof window !== "undefined" ? window.innerWidth : 1200) * 0.85));

function lockMessage(lock: ISchemaJobLock) {
    const job = lockJobPhrase(lock);
    if (lock.broken) {
        return `The lock of ${job} was broken. The schema is free from ${formatDate(lock.leaseUntilDate)} (plus up to 30 seconds).`;
    }
    if (lock.expired) {
        return `The ${job} stopped renewing its lock on this schema; the next job to need the schema takes it over.`;
    }
    return `The ${job} is working on this schema since ${formatDate(lock.acquiredDate)}. Other deduplication and duplicate search jobs on it wait until it finishes.`;
}

// A decision on the item, as the Decisions timeline shows it.
function decisionText(entry: NonNullable<IReviewQueueItem["history"]>[number]) {
    const reason = [reviewReasonCodeLabel(entry.resolutionReasonCode), entry.resolutionReason].filter(Boolean).join(": ");
    return `${entry.status}${entry.resolution ? ` (${entry.resolution})` : ""} by ${entry.resolvedBy || "the system"} on ${formatDate(entry.resolvedDate)}${reason ? `: ${reason}` : ""}`;
}

// The review queue of one schema: the clusters deduplication could not act on, with their records
// side by side. Stewards (goldenstewardship review) merge or dismiss open items from here.
const ReviewQueue = ({ tenant, businessArea, schema, userBusinessArea, setBreadcrumbNavigationItems }: IReviewQueueProps) => {
    const dispatch = useAppDispatch();
    const schemaModel = useAppSelector((state) => state.schemaModels.schemaModel);
    const {
        items,
        pageKey,
        loadingItems,
        getItemsResult,
        counts,
        item,
        itemRecords,
        loadingItem,
        getItemResult,
        lock,
        breakingLock,
        breakLockResult,
        actingOnItem,
        itemActionResult,
    } = useAppSelector((state) => state.reviewQueue);
    const { canBreakLock, canReview } = useAppSelector((state) => state.goldenNominal.goldenStewardshipPermission);
    const [filters, setFilters] = React.useState<{ status: string; type?: string }>({ status: "OPEN" });
    // The review items, or the records deduplication flagged as subsumed (PRE-2837); a link may ask
    // for the latter (?view=subsumed).
    const [view, setView] = React.useState<"items" | "subsumed">(() => (new URLSearchParams(window.location.search).get("view") === "subsumed" ? "subsumed" : "items"));
    const [openItemId, setOpenItemId] = React.useState<string | null>(null);
    // The action a steward is taking, on the item (and its records) as the dialog shows it, and
    // why the last try failed. The dialog keeps them while the item is read again after a conflict.
    const [pendingAction, setPendingAction] = React.useState<string | null>(null);
    const [actionItem, setActionItem] = React.useState<{ item: IReviewQueueItem; records: { [dataObjectId: string]: any } } | null>(null);
    const [actionError, setActionError] = React.useState<string | null>(null);

    const businessAreaId = userBusinessArea?.businessAreaId;
    const schemaId = schemaModel?.schemaId;
    const schemaCounts = counts && counts.schemaId === schemaId ? counts : null;

    React.useEffect(() => {
        dispatch(getGoldenStewardshipPermissionRequest());
    }, [dispatch]);

    // A schema's counts and lock: read when the schema is shown, not on every filter change.
    const refreshSchema = React.useCallback(() => {
        if (businessAreaId && schemaId) {
            dispatch(getReviewQueueCountsRequest(businessAreaId, schemaId));
            dispatch(getSchemaJobLockRequest(businessAreaId, schemaId));
        }
    }, [dispatch, businessAreaId, schemaId]);

    const refreshList = React.useCallback(() => {
        if (businessAreaId && schemaId) {
            dispatch(getReviewQueueItemsRequest(businessAreaId, schemaId, filters));
        }
    }, [dispatch, businessAreaId, schemaId, filters]);

    // Nothing of another schema's queue (its lock, errors, break outcome) stays on screen.
    React.useEffect(() => {
        dispatch(resetReviewQueue());
        refreshSchema();
    }, [dispatch, refreshSchema]);

    React.useEffect(() => {
        refreshList();
    }, [refreshList]);

    const refresh = () => {
        refreshSchema();
        refreshList();
    };

    React.useEffect(() => {
        if (setBreadcrumbNavigationItems && schemaModel) {
            const base = AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", tenant.toLowerCase()).replace(":businessarea", businessArea.toLowerCase());
            setBreadcrumbNavigationItems([
                { route: AppPaths.TENANT_HOME.replace(":tenant", tenant.toLowerCase()), text: "Home" },
                { route: base, text: "Schemas" },
                { route: `${base}#${schemaModel.name}`, text: schemaModel.name },
                { route: "", text: "Review queue" },
            ]);
        }
    }, [setBreadcrumbNavigationItems, schemaModel, tenant, businessArea]);

    // Shown once: the outcome is cleared as soon as it has been shown.
    React.useEffect(() => {
        if (!breakLockResult) {
            return;
        }
        if (breakLockResult.success) {
            message.success(`Lock broken. The job stops within 30 seconds; the schema is free from ${formatDate(breakLockResult.availableDate)}.`);
        } else {
            message.error(breakLockResult.message || "The lock could not be broken.");
        }
        dispatch(clearSchemaJobLockBreakResult());
    }, [dispatch, breakLockResult]);

    // Shown once: a success closes the item and reads the list again; a failure stays in the
    // dialog (after a conflict the item has been read again, for the steward to decide anew).
    React.useEffect(() => {
        if (!itemActionResult) {
            return;
        }
        if (itemActionResult.success) {
            const done = {
                [REVIEW_ACTIONS.DISMISS]: "Review item dismissed.",
                [REVIEW_ACTIONS.BIRTH]: "Golden record created.",
                [REVIEW_ACTIONS.SUBSUME]: "Duplicate flagged as subsumed. Retire it from Subsumed records.",
            } as { [action: string]: string };
            message.success(done[itemActionResult.action] || "Review item resolved.");
            endAction();
            setOpenItemId(null);
            refreshList();
        } else {
            setActionError(itemActionResult.message || "The action could not be completed.");
        }
        dispatch(clearReviewItemActionResult());
    }, [dispatch, itemActionResult, refreshList]);

    const endAction = () => {
        setPendingAction(null);
        setActionItem(null);
        setActionError(null);
    };

    // The item read again (after a conflict): the dialog shows it as it now is, or closes when the
    // item no longer allows the action (someone else closed it); a failed read closes it too.
    React.useEffect(() => {
        if (!pendingAction || !actionItem) {
            return;
        }
        if (!getItemResult.success) {
            endAction();
        } else if (item && item.itemId === actionItem.item.itemId && item !== actionItem.item) {
            if (canTakeAction(item, pendingAction)) {
                setActionItem({ item, records: itemRecords });
            } else {
                message.warning(`This item is no longer open (${item.status.toLowerCase()}).`);
                endAction();
            }
        }
    }, [item, itemRecords, getItemResult, pendingAction, actionItem]);

    const startAction = (action: string) => {
        if (!item) {
            return;
        }
        setActionError(null);
        setActionItem({ item, records: itemRecords });
        setPendingAction(action);
    };

    const submitAction = (request: IReviewItemActionRequest) => {
        if (businessAreaId && schemaId && actionItem && pendingAction) {
            setActionError(null);
            dispatch(reviewItemActionRequest(businessAreaId, schemaId, actionItem.item.itemId, pendingAction, request));
        }
    };

    const openItem = (itemId: string) => {
        setOpenItemId(itemId);
        if (businessAreaId && schemaId) {
            dispatch(getReviewQueueItemRequest(businessAreaId, schemaId, itemId));
        }
    };

    const closeItem = () => {
        endAction();
        setOpenItemId(null);
    };

    // Cleared once the drawer has closed, so it does not go blank while it slides out.
    const onDrawerOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(clearReviewQueueItem());
        }
    };

    const loadMore = () => {
        if (businessAreaId && schemaId && pageKey) {
            dispatch(getReviewQueueItemsRequest(businessAreaId, schemaId, filters, pageKey, true));
        }
    };

    const breakLock = () => {
        if (businessAreaId && schemaId && lock?.jobId) {
            dispatch(breakSchemaJobLockRequest(businessAreaId, schemaId, lock.jobId));
        }
    };

    const columns = [
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            render: (type: string) => (
                <Tooltip title={reviewTypeHelp(type)}>
                    <Tag color="purple">{reviewTypeLabel(type)}</Tag>
                </Tooltip>
            ),
        },
        { title: "Reason", dataIndex: "reason", key: "reason", render: (reason: string) => reviewReasonLabel(reason) },
        {
            title: "Records",
            key: "records",
            render: (_: unknown, row: IReviewQueueItem) => {
                const hidden = row.members.filter((member) => member.redacted).length;
                return hidden > 0 ? `${row.members.length} (${hidden} hidden)` : row.members.length;
            },
        },
        { title: "Search run", dataIndex: "setVersion", key: "setVersion" },
        { title: "Raised", dataIndex: "createdDate", key: "createdDate", render: (value: string) => formatDate(value) },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: string, row: IReviewQueueItem) => (
                <Tooltip title={row.resolutionReason || undefined}>
                    <Tag color={reviewStatusColor(status)}>{status}</Tag>
                </Tooltip>
            ),
        },
        {
            key: "view",
            render: (_: unknown, row: IReviewQueueItem) => (
                <Button type="link" onClick={() => openItem(row.itemId)} data-testid="review-queue-view-item-button">
                    View
                </Button>
            ),
        },
    ];

    const denied = !getItemsResult.success && getItemsResult.code === "PERMISSION_DENIED";

    if (denied) {
        return (
            <div data-testid="review-queue">
                <Alert type="error" showIcon data-testid="review-queue-error" title="You do not have permission to see this schema's review queue." />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3" data-testid="review-queue">
            <Space className="w-full justify-between" wrap>
                <Space align="center">
                    <Typography.Title level={3} className="!mb-0">
                        Review queue
                    </Typography.Title>
                    {schemaCounts && <Badge count={schemaCounts.open} showZero color="purple" title="Open items" />}
                    <Segmented
                        value={view}
                        options={[
                            { label: "Review items", value: "items" },
                            { label: "Subsumed records", value: "subsumed" },
                        ]}
                        onChange={(value) => setView(value as "items" | "subsumed")}
                        data-testid="review-queue-view"
                    />
                </Space>
                <Space wrap className={view === "items" ? undefined : "!hidden"}>
                    <Select
                        value={filters.status}
                        style={{ width: 150 }}
                        options={REVIEW_STATUSES.map((status) => ({ label: status, value: status }))}
                        onChange={(status) => setFilters({ ...filters, status })}
                        data-testid="review-queue-status-filter"
                    />
                    <Select
                        allowClear
                        placeholder="All types"
                        value={filters.type}
                        style={{ width: 220 }}
                        options={REVIEW_TYPES.map((type) => ({
                            label: `${reviewTypeLabel(type)}${schemaCounts?.openByType?.[type] ? ` (${schemaCounts.openByType[type]} open)` : ""}`,
                            value: type,
                        }))}
                        onChange={(type) => setFilters({ ...filters, type })}
                        data-testid="review-queue-type-filter"
                    />
                    <Button icon={<ReloadOutlined />} onClick={refresh} data-testid="review-queue-refresh-button">
                        Refresh
                    </Button>
                </Space>
            </Space>

            {lock?.locked && (
                <Alert
                    type={lock.broken ? "warning" : "info"}
                    showIcon
                    icon={<LockOutlined />}
                    data-testid="review-queue-lock-alert"
                    title={lockMessage(lock)}
                    action={
                        canBreakLock && !lock.broken && !lock.expired ? (
                            <Popconfirm
                                title="Break this lock?"
                                description="Only for a job that is stuck. It stops within 30 seconds, and the schema is free once its lease runs out."
                                okText="Break lock"
                                okButtonProps={{ danger: true }}
                                onConfirm={breakLock}
                            >
                                <Button danger size="small" loading={breakingLock} data-testid="review-queue-break-lock-button">
                                    Break lock
                                </Button>
                            </Popconfirm>
                        ) : undefined
                    }
                />
            )}

            {view === "subsumed" && <SubsumedRecords key={schemaId} businessAreaId={businessAreaId} schemaId={schemaId} canReview={!!canReview} />}

            {view === "items" && !getItemsResult.success && (
                <Alert type="error" showIcon data-testid="review-queue-error" title={getItemsResult.message || "The review queue could not be loaded."} />
            )}

            {view === "items" && (
                <Table
                    size="small"
                    rowKey="itemId"
                    loading={loadingItems}
                    dataSource={items}
                    columns={columns}
                    pagination={false}
                    locale={{ emptyText: filters.status === "OPEN" ? "Nothing to review for this schema." : "No items." }}
                    data-testid="review-queue-table"
                />
            )}
            {view === "items" && pageKey && (
                <Button onClick={loadMore} loading={loadingItems} data-testid="review-queue-load-more-button">
                    Load more
                </Button>
            )}

            <Drawer
                open={!!openItemId}
                onClose={closeItem}
                afterOpenChange={onDrawerOpenChange}
                size={drawerWidth()}
                title="Review item"
                destroyOnHidden
                data-testid="review-queue-item-drawer"
            >
                <Spin spinning={loadingItem}>
                    {!getItemResult.success && <Alert type="error" showIcon title={getItemResult.message || "The review item could not be loaded."} />}
                    {item && (
                        <div className="flex flex-col gap-4">
                            <Space wrap>
                                <Tag color="purple">{reviewTypeLabel(item.type)}</Tag>
                                <Tag color={reviewStatusColor(item.status)}>{item.status}</Tag>
                                {item.reason && <span>{reviewReasonLabel(item.reason)}</span>}
                                <Typography.Text type="secondary">
                                    Search run {item.setVersion} · raised {formatDate(item.createdDate)}
                                </Typography.Text>
                            </Space>
                            <Typography.Paragraph type="secondary" className="!mb-0">
                                {reviewTypeHelp(item.type)}
                            </Typography.Paragraph>
                            <ReviewItemComparison item={item} records={itemRecords} fields={schemaModel?.fields || []} />
                            {canReview &&
                                [REVIEW_ACTIONS.MERGE, REVIEW_ACTIONS.BIRTH, REVIEW_ACTIONS.SUBSUME, REVIEW_ACTIONS.DISMISS].some((action) => canTakeAction(item, action)) && (
                                    <Space>
                                        {canTakeAction(item, REVIEW_ACTIONS.SUBSUME) && (
                                            <Button type="primary" onClick={() => startAction(REVIEW_ACTIONS.SUBSUME)} data-testid="review-item-subsume-button">
                                                Flag as subsumed
                                            </Button>
                                        )}
                                        {canTakeAction(item, REVIEW_ACTIONS.BIRTH) && (
                                            <Button type="primary" onClick={() => startAction(REVIEW_ACTIONS.BIRTH)} data-testid="review-item-birth-button">
                                                Create golden record
                                            </Button>
                                        )}
                                        {canTakeAction(item, REVIEW_ACTIONS.MERGE) && (
                                            <Button type="primary" onClick={() => startAction(REVIEW_ACTIONS.MERGE)} data-testid="review-item-merge-button">
                                                {item.type === "STEWARD_CONFLICT" ? "Resolve" : "Merge"}
                                            </Button>
                                        )}
                                        {canTakeAction(item, REVIEW_ACTIONS.DISMISS) && (
                                            <Button onClick={() => startAction(REVIEW_ACTIONS.DISMISS)} data-testid="review-item-dismiss-button">
                                                Dismiss
                                            </Button>
                                        )}
                                    </Space>
                                )}
                            {(item.history?.length || 0) > 0 || item.resolvedDate ? (
                                <div>
                                    <Typography.Title level={5}>Decisions</Typography.Title>
                                    <Timeline items={[...(item.history || []), ...(item.resolvedDate ? [item] : [])].map((entry) => ({ content: decisionText(entry) }))} />
                                </div>
                            ) : null}
                        </div>
                    )}
                </Spin>
            </Drawer>
            {actionItem && (
                <ReviewItemActionModal
                    action={pendingAction}
                    item={actionItem.item}
                    records={actionItem.records}
                    fields={schemaModel?.fields || []}
                    acting={actingOnItem}
                    errorMessage={actionError}
                    onSubmit={submitAction}
                    onCancel={endAction}
                />
            )}
        </div>
    );
};

export default ReviewQueue;
